/**
 * How a school of fish moves within itself (M88, the user's word: "for the
 * dynamic fish school movement, base their movements off of how schools of
 * fish actually move"). Pure arithmetic in body lengths, so it is tested in
 * node (`tests/schooling.test.ts`); `fishSchools.ts` moves each school's
 * middle along its commute (`fishSwim.ts`) and lays these places about it.
 *
 * **The rules**: each fish keeps away from any neighbor nearer than
 * `REPEL_BL`, and otherwise turns to swim as its neighbors within
 * `ORIENT_BL` swim and toward those within `ATTRACT_BL`, as the zonal models
 * of fish schools have it (repulsion first, then orientation and
 * attraction; Couzin et al. 2002 is the model's usual reference, not read
 * here: the environment refused its hosts, as it did every paper with
 * measured interaction rules, `docs/KNOWN_ISSUES.md` M88); each also pulled
 * toward the school's middle by `GOAL_WEIGHT` (every fish knowing where the
 * school is bound), turning at most `TURN_PER_S`, jostled by a little noise.
 * **The spacing**, MEASURED: a fish's nearest neighbor 1.2 body lengths
 * away on average ("the mean of the mean distance to the nearest neighbor
 * was 1.2 body lengths with s = 0.3 body length", Graves 1977, Fishery
 * Bulletin 75: 232, anchovy schools photographed at sea; no reef fish's was
 * served), which `REPEL_BL` is set to give (`tests/schooling.test.ts`).
 * **Feeding**: a parrotfish foraging group "swam about 1 m above the
 * substrate [...] and descended en masse at intervals to feed" (Colin 1978,
 * Fishery Bulletin 76: 119, the striped parrotfish at Jamaica): the whole
 * school drops to the reef together every `FEED_EVERY_S` for `FEED_FOR_S`
 * (`feeding`). **Terminal males** hold territories round their harems ("Large
 * terminal-phase male bullethead parrotfish C. spilurus establish feeding
 * territories with female harems along the reef slope at ≥ 20 m depth",
 * Tricas and Boyle 2014, Mar Ecol Prog Ser 511: 8; the palenose's adult
 * males' "completely non-overlapping core use areas in 5 of the 6 possible
 * combinations", Annandale et al. 2024, Front Fish Sci 2, p. 8): each
 * patrols alone, back and forth along its own arc round its school
 * (`patrol`).
 */

/** A fish in its school, body lengths from the school's middle, its heading a unit vector, on the level (x east, z south). */
export interface Swimmer {
  x: number;
  z: number;
  hx: number;
  hz: number;
  /**
   * Its own ways among its school (M265, the interactions brief's section 5), each as the school's where not given: its
   * pace a share of the school's (a little quicker or slower than its neighbors, eased), its room a share of `REPEL_BL`
   * (spacing that varies), the schoolmate it follows for now (its index; its heading weighed `FOLLOW_WEIGHT` more), and
   * a goal of its own off the school's middle, body lengths (a few drifting apart a while and rejoining).
   */
  pace?: number;
  space?: number;
  lead?: number;
  /** The window of its own its lead was chosen in (`fishSchools.ts`'s `SCHOOL_LIFE.leadS`), so it keeps it through it. */
  leadWindow?: number;
  goal?: readonly [number, number];
}

/** Body lengths within which a fish turns away from a neighbor first of all (set to give the measured 1.2 body lengths to the nearest, MEASURED by the test). */
export const REPEL_BL = 1.2;
/**
 * Body lengths within which it swims as its neighbors swim, on its commute and while it forages, and within
 * which it turns toward them (JUDGED: a school a few lengths across holds together, aligned as it travels
 * and milling loosely as it feeds, as the zonal models' wide and narrow zones of orientation give).
 */
export const ORIENT_BL = 3;
export const FORAGE_ORIENT_BL = 1.5;
const ATTRACT_BL = 8;
/** How strongly each fish turns toward the school's middle, against its neighbors' pull (JUDGED: the school stays round its commute's point). */
const GOAL_WEIGHT = 0.35;
/**
 * How strongly a fish with a goal of its own turns toward it (M265, `Swimmer.goal`), and it swims with and toward only
 * the others sharing a goal (JUDGED: at `GOAL_WEIGHT` against its whole school's pull its group stayed in it, 1.8
 * lengths the wrong way in the test's 20 s, the first draft's).
 */
const SPLIT_WEIGHT = 1.2;
/** The fastest it turns, radians a second, and how much it wanders, radians a second's root (JUDGED: no reef fish's turning was served). */
const TURN_PER_S = 2.5;
const NOISE_PER_ROOT_S = 0.6;
/**
 * Its speed in body lengths a second, foraging and on its commute (JUDGED: no parrotfish's speed was
 * served; the commute's within the 0.6 to 2.6 lengths a second of Meyer's 500 to 1,800 m swum in an hour about
 * sunrise or sunset, the hour JUDGED, for an initial phase of 191 to 243 mm, `fishKinds.ts`).
 */
export const FORAGE_BL_PER_S = 0.6;
export const COMMUTE_BL_PER_S = 1.2;
/**
 * Its speed while it shelters at night, body lengths a second (JUDGED, at the user's word, M88: "the fish should
 * be moving continuously, not stop and go like it is now"; a sheltering fish barely moves, so it mills at a
 * quarter of its foraging pace where before it lay still).
 */
export const NIGHT_BL_PER_S = 0.15;
/**
 * How often the school drops to the reef to feed, and for how long, seconds (JUDGED: Colin gives "at intervals", no times;
 * a third of its time, as M88 set it, its bouts lengthened in M159 from 3 s of every 9, so its fish, rising and sinking
 * no faster than they forage, `fishSchools.ts`'s `RISE_BL_PER_S`, reach the reef and feed: the user's report, "a many of
 * them move vertically in a choppy rapid manner", the dive 1.2 lengths in about half a second).
 */
export const FEED_EVERY_S = 18;
export const FEED_FOR_S = 6;
/** Body lengths two fish are never drawn nearer than: a fish that cannot turn away in time is set apart (JUDGED: no two pictures over each other). */
export const APART_BL = 0.6;
/** How much more a fish swims as the schoolmate it follows swims than as its other neighbors (M265; JUDGED: it holds to its leader's way through a turn, a little behind the school's). */
export const FOLLOW_WEIGHT = 1.5;
/** The longest step taken at once, seconds, so a quick frame never jumps the rules. */
export const STEP_S = 0.05;

/** A small seeded stream (mulberry32) for the noise, one a school, so the same school jostles the same way. */
export function noiseStream(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), a | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** A unit vector's turn toward another by at most `max` radians, as [x, z]. */
function turned(hx: number, hz: number, tx: number, tz: number, max: number): [number, number] {
  const want = Math.atan2(tz, tx);
  const now = Math.atan2(hz, hx);
  let d = want - now;
  d = Math.atan2(Math.sin(d), Math.cos(d));
  const a = now + Math.max(-max, Math.min(max, d));
  return [Math.cos(a), Math.sin(a)];
}

/**
 * One step of `dt` seconds for a school's fish, in place: each turns by the
 * rules toward the way it wants, then swims on at `speed` body lengths a
 * second. `travel` is the way the school as a whole is bound (a vector
 * along it as long as how much the school is travelling, 0 to 1, or zero
 * while it stays), which every fish knows and leans to, and
 * `orient` how far it looks to swim as its neighbors swim; the
 * places are the school's own, about its middle, so a school on its commute
 * keeps its shape while its middle is carried along.
 */
export function stepSchool(fish: Swimmer[], dt: number, speed: number, travel: [number, number], rng: () => number, orient = ORIENT_BL): void {
  const next = fish.map((f, i) => {
    let rx = 0;
    let rz = 0;
    let ox = 0;
    let oz = 0;
    let ax = 0;
    let az = 0;
    for (let j = 0; j < fish.length; j++) {
      if (j === i) continue;
      const g = fish[j] as Swimmer;
      const dx = g.x - f.x;
      const dz = g.z - f.z;
      const d = Math.hypot(dx, dz);
      if (d < 1e-9) continue;
      // The room between two the mean of their own (M265's: each its own, a roomier fish backed off neighbors that kept
      // coming, and its school spread to twice its width).
      const room = (REPEL_BL * ((f.space ?? 1) + (g.space ?? 1))) / 2;
      if (d < room) {
        rx -= dx / d;
        rz -= dz / d;
      } else if (!f.goal !== !g.goal) {
        // Apart a while: kept off the others, swimming with its own group alone (M265).
        continue;
      } else if (d < orient) {
        ox += g.hx;
        oz += g.hz;
      } else if (d < ATTRACT_BL) {
        ax += dx / d;
        az += dz / d;
      }
    }
    // The schoolmate it follows for now, its way weighed the more (M265).
    const lead = f.lead !== undefined && f.lead !== i ? fish[f.lead] : undefined;
    if (lead) {
      ox += FOLLOW_WEIGHT * lead.hx;
      oz += FOLLOW_WEIGHT * lead.hz;
    }
    let [tx, tz] = rx !== 0 || rz !== 0 ? [rx, rz] : [ox + ax + f.hx, oz + az + f.hz];
    const tl = Math.hypot(tx, tz) || 1;
    tx /= tl;
    tz /= tl;
    // Every fish knows where the school is bound: its middle (or its own goal off it, M265), and the way it travels.
    const [gx, gz] = [f.x - (f.goal?.[0] ?? 0), f.z - (f.goal?.[1] ?? 0)];
    const toMiddle = Math.hypot(gx, gz);
    const pull = (f.goal ? SPLIT_WEIGHT : GOAL_WEIGHT) * Math.min(1, toMiddle / ORIENT_BL);
    if (toMiddle > 1e-9) {
      tx -= (pull * gx) / toMiddle;
      tz -= (pull * gz) / toMiddle;
    }
    tx += travel[0];
    tz += travel[1];
    const noise = NOISE_PER_ROOT_S * Math.sqrt(dt) * (rng() * 2 - 1);
    const [hx, hz] = turned(f.hx, f.hz, tx, tz, TURN_PER_S * dt);
    const c = Math.cos(noise);
    const s = Math.sin(noise);
    return { hx: hx * c - hz * s, hz: hx * s + hz * c };
  });
  // The school's own travel is its middle's: each fish's place moves by its swim less the school's.
  next.forEach((h, i) => {
    const f = fish[i] as Swimmer;
    f.hx = h.hx;
    f.hz = h.hz;
    const own = speed * (f.pace ?? 1);
    f.x += (h.hx - travel[0]) * own * dt;
    f.z += (h.hz - travel[1]) * own * dt;
  });
  // Any two nearer than `APART_BL` set apart, each half the shortfall.
  for (let i = 0; i < fish.length; i++) {
    for (let j = i + 1; j < fish.length; j++) {
      const a = fish[i] as Swimmer;
      const b = fish[j] as Swimmer;
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const d = Math.hypot(dx, dz);
      if (d >= APART_BL) continue;
      const [ux, uz] = d > 1e-9 ? [dx / d, dz / d] : [1, 0];
      const push = (APART_BL - d) / 2;
      a.x -= ux * push;
      a.z -= uz * push;
      b.x += ux * push;
      b.z += uz * push;
    }
  }
}

/**
 * The number of a school's feeding dives whose foot (`feeding` at 1, down on the reef) it has passed by `seconds`: one
 * more than before once a dive's foot is passed (M173, the parrotfish's bites sending up their sediment).
 */
export function divesDown(seconds: number, phase: number): number {
  return Math.floor((seconds + phase * FEED_EVERY_S - FEED_FOR_S / 2) / FEED_EVERY_S);
}

/** How far below its cruising height a school swims at `seconds` into its day out, 0 cruising to 1 down on the reef feeding, eased in and out. */
export function feeding(seconds: number, phase: number): number {
  const t = (((seconds + phase * FEED_EVERY_S) % FEED_EVERY_S) + FEED_EVERY_S) % FEED_EVERY_S;
  // Down and up again smoothly, its fastest no more than its foraging pace (M159: `min(1, 2 sin)` dived in a sixth of
  // the bout).
  return t < FEED_FOR_S ? Math.sin((Math.PI * t) / FEED_FOR_S) ** 2 : 0;
}

/** Body lengths beyond the school's middle a terminal male patrols, and the seconds of its beat back and forth (JUDGED). */
export const TERRITORY_BL = 5;
export const PATROL_S = 14;

/**
 * Where a terminal male patrols at `seconds`: back and forth along its own
 * arc round its school, the `k`th of `of` males splitting the circle so no
 * two share ground, each beat timed by its own `phase` (0 to 1).
 */
export function patrol(k: number, of: number, seconds: number, phase: number): [number, number] {
  const sector = (2 * Math.PI) / Math.max(1, of);
  const angle = sector * (k + 0.5) + 0.4 * sector * Math.sin((2 * Math.PI * (seconds / PATROL_S + phase)));
  const r = TERRITORY_BL * (1 + 0.15 * Math.sin(2 * Math.PI * (seconds / (PATROL_S * 0.7) + phase)));
  return [r * Math.cos(angle), r * Math.sin(angle)];
}
